/**
 * useViewportSize — reads visualViewport dimensions (mobile keyboard-aware).
 *
 * Falls back to window.innerWidth / window.innerHeight on browsers that
 * don't support VisualViewport API (rare in 2024+ but safe to guard).
 *
 * Use this instead of CSS `@media (max-width: 768px)` when you need the
 * JS equivalent — e.g. to conditionally mount mobile-only components.
 */

import { useEffect, useState } from "react";

export interface ViewportSize {
  width: number;
  height: number;
  /** True when width ≤ 768 (mobile / small tablet). */
  isMobile: boolean;
  /** True when width ≤ 480 (compact / phone). */
  isCompact: boolean;
}

const SSR_DEFAULT: ViewportSize = {
  width: 375,
  height: 812,
  isMobile: true,
  isCompact: false,
};

function readViewport(): ViewportSize {
  if (typeof window === "undefined") return SSR_DEFAULT;
  const vv = window.visualViewport;
  const width = vv ? vv.width : window.innerWidth;
  const height = vv ? vv.height : window.innerHeight;
  return {
    width,
    height,
    isMobile: width <= 768,
    isCompact: width <= 480,
  };
}

export function useViewportSize(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(readViewport);

  useEffect(() => {
    function update() {
      setSize(readViewport());
    }

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    } else {
      window.addEventListener("resize", update);
    }

    update();

    return () => {
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      } else {
        window.removeEventListener("resize", update);
      }
    };
  }, []);

  return size;
}
